import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AchievementBadgeGallery = ({ user, onShareAchievement }) => {
  const [allAchievements, setAllAchievements] = useState([]);
  const [unlockedAchievements, setUnlockedAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    if (user) {
      loadAchievements();
    }
  }, [user]);

  const loadAchievements = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('session_token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Load all available achievements
      const definitionsResponse = await axios.get(`${API}/achievements/definitions`, { headers });
      
      // Load user's unlocked achievements
      const userResponse = await axios.get(`${API}/achievements/user`, { headers });
      
      setAllAchievements(definitionsResponse.data.achievements || []);
      setUnlockedAchievements(userResponse.data.unlocked_achievements || []);
      
    } catch (err) {
      console.error('Error loading achievements:', err);
      setError('Failed to load achievement gallery. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      milestone: 'from-purple-400 to-purple-600',
      creative: 'from-blue-400 to-blue-600',
      engagement: 'from-green-400 to-green-600',
      social: 'from-orange-400 to-orange-600',
      streak: 'from-red-400 to-red-600',
    };
    return colors[category] || 'from-gray-400 to-gray-600';
  };

  const getBorderColor = (category) => {
    const colors = {
      milestone: 'border-purple-300',
      creative: 'border-blue-300',
      engagement: 'border-green-300',
      social: 'border-orange-300',
      streak: 'border-red-300',
    };
    return colors[category] || 'border-gray-300';
  };

  const isAchievementUnlocked = (achievementId) => {
    return unlockedAchievements.some(unlocked => unlocked.achievement_id === achievementId);
  };

  const getUnlockedDate = (achievementId) => {
    const unlocked = unlockedAchievements.find(u => u.achievement_id === achievementId);
    return unlocked ? new Date(unlocked.unlocked_at).toLocaleDateString() : null;
  };

  const filteredAchievements = allAchievements.filter(achievement => {
    if (activeCategory === 'all') return true;
    if (activeCategory === 'unlocked') return isAchievementUnlocked(achievement.achievement_id);
    if (activeCategory === 'locked') return !isAchievementUnlocked(achievement.achievement_id);
    return achievement.category === activeCategory;
  });

  const categories = [...new Set(allAchievements.map(a => a.category))];
  const unlockedCount = unlockedAchievements.length;
  const totalCount = allAchievements.length;
  const progressPercentage = totalCount > 0 ? Math.round((unlockedCount / totalCount) * 100) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 mb-4">⚠️</div>
        <p className="text-gray-600 dark:text-gray-400">{error}</p>
        <button
          onClick={loadAchievements}
          className="mt-4 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header with Progress */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">🏅 Achievement Gallery</h2>
            <p className="text-gray-600 dark:text-gray-400">Collect badges by completing activities and reaching milestones</p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-orange-500">{unlockedCount}/{totalCount}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Achievements</div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
            <span>Progress</span>
            <span>{progressPercentage}%</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
            <div 
              className="bg-gradient-to-r from-orange-400 to-orange-600 h-3 rounded-full transition-all duration-500"
              style={{ width: `${progressPercentage}%` }}
            ></div>
          </div>
        </div>

        {/* Category Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {categories.map(category => {
            const categoryAchievements = allAchievements.filter(a => a.category === category);
            const categoryUnlocked = categoryAchievements.filter(a => isAchievementUnlocked(a.achievement_id));
            return (
              <div key={category} className="text-center">
                <div className={`inline-flex px-3 py-1 rounded-full text-sm font-medium border ${getBorderColor(category)} bg-white dark:bg-gray-700`}>
                  {category}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {categoryUnlocked.length}/{categoryAchievements.length}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {['all', 'unlocked', 'locked', ...categories].map((filter) => (
          <button
            key={filter}
            onClick={() => setActiveCategory(filter)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeCategory === filter
                ? 'bg-orange-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            {filter.charAt(0).toUpperCase() + filter.slice(1)}
          </button>
        ))}
      </div>

      {/* Achievement Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
        {filteredAchievements.map((achievement) => {
          const isUnlocked = isAchievementUnlocked(achievement.achievement_id);
          const unlockedDate = getUnlockedDate(achievement.achievement_id);
          
          return (
            <div
              key={achievement.achievement_id}
              className={`relative bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 transition-all duration-300 hover:shadow-xl ${
                isUnlocked ? 'ring-2 ring-orange-200' : 'opacity-60'
              }`}
            >
              {/* Achievement Badge */}
              <div className="relative mb-4">
                <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center text-2xl ${
                  isUnlocked 
                    ? `bg-gradient-to-br ${getCategoryColor(achievement.category)} text-white shadow-lg`
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-400'
                }`}>
                  {isUnlocked ? achievement.badge_icon : '🔒'}
                </div>
                
                {/* Unlock indicator */}
                {isUnlocked && (
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs">✓</span>
                  </div>
                )}
              </div>

              {/* Achievement Info */}
              <div className="text-center">
                <h3 className={`font-bold mb-2 ${
                  isUnlocked ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'
                }`}>
                  {achievement.name}
                </h3>
                
                <p className={`text-sm mb-3 ${
                  isUnlocked ? 'text-gray-600 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500'
                }`}>
                  {achievement.description}
                </p>

                {/* Category and Points */}
                <div className="flex items-center justify-between text-xs">
                  <span className={`px-2 py-1 rounded-full border ${
                    isUnlocked 
                      ? `${getBorderColor(achievement.category)} bg-white dark:bg-gray-700` 
                      : 'border-gray-300 bg-gray-100 dark:bg-gray-600'
                  }`}>
                    {achievement.category}
                  </span>
                  <span className={`font-semibold ${
                    isUnlocked ? 'text-orange-500' : 'text-gray-400'
                  }`}>
                    {achievement.points}pts
                  </span>
                </div>

                {/* Unlock Date */}
                {isUnlocked && unlockedDate && (
                  <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Unlocked: {unlockedDate}
                  </div>
                )}

                {/* Share Button for Unlocked Achievements */}
                {isUnlocked && onShareAchievement && (
                  <div className="mt-2">
                    <button
                      onClick={() => onShareAchievement({
                        ...achievement,
                        unlocked_at: unlockedDate
                      })}
                      className="text-xs bg-orange-500 text-white px-2 py-1 rounded-full hover:bg-orange-600 transition-colors"
                    >
                      📤 Share
                    </button>
                  </div>
                )}
              </div>

              {/* Locked Overlay */}
              {!isUnlocked && (
                <div className="absolute inset-0 bg-gray-900 bg-opacity-10 rounded-xl flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-3xl mb-2">🔒</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 px-2">
                      Keep creating to unlock!
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filteredAchievements.length === 0 && (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🎯</div>
          <p className="text-gray-600 dark:text-gray-400">No achievements found for this filter.</p>
        </div>
      )}
    </div>
  );
};

export default AchievementBadgeGallery;